<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'

defineProps<{
  collapsed?: boolean
}>()

const colorMode = useColorMode()
const { user: authUser, handleSignOut, getUserDisplayName, userClaims, isAuthenticated } = useAuth()
const { isDemoMode } = useDemo()
const { isAdmin, adminUser, clearTokens: clearAdminTokens } = useAdminAuth()
const { clearCustomer } = useImpersonation()

const user = computed(() => {
  // Check for admin user first (Google OAuth login)
  if (isAdmin.value && adminUser.value) {
    const displayName = adminUser.value.name || adminUser.value.email || 'Admin'
    return {
      name: displayName,
      email: adminUser.value.email || '',
      avatar: {
        src: `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=7c3aed`,
        alt: displayName
      },
      isAdmin: true
    }
  }

  // Only show user data if authenticated (real or demo mode)
  if (!isAuthenticated.value && !isDemoMode.value) {
    return null
  }

  if (authUser.value) {
    const displayName = getUserDisplayName() || authUser.value.email || 'User'
    return {
      name: displayName,
      email: userClaims.value?.email || authUser.value.email || '',
      avatar: {
        src: `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`,
        alt: displayName
      },
      isAdmin: false
    }
  }

  // In demo mode without auth data yet, show loading state
  if (isDemoMode.value) {
    return {
      name: 'Demo User',
      email: 'demo@example.com',
      avatar: {
        src: 'https://ui-avatars.com/api/?name=Demo+User&background=random',
        alt: 'Demo User'
      },
      isAdmin: false
    }
  }

  return null
})

async function logout() {
  // Clear admin tokens and impersonation state if admin user
  if (user.value?.isAdmin) {
    clearCustomer()
    await clearAdminTokens()
    navigateTo('/admin')
    return
  }
  await handleSignOut()
}

const emit = defineEmits<{
  openCookieSettings: []
  switchAccount: []
}>()

const items = computed<DropdownMenuItem[][]>(() => {
  if (!user.value) return []

  return [[{
    type: 'label',
    label: user.value.name,
    avatar: user.value.avatar
  }], [{
    label: 'Switch Account',
    icon: 'i-lucide-repeat',
    onSelect() {
      emit('switchAccount')
    }
  }, {
    label: 'Settings',
    icon: 'i-lucide-settings',
    to: '/settings'
  }, {
    label: 'Appearance',
    icon: 'i-lucide-sun-moon',
    children: [{
      label: 'Light',
      icon: 'i-lucide-sun',
      type: 'checkbox',
      checked: colorMode.value === 'light',
      onSelect(e: Event) {
        e.preventDefault()

        colorMode.preference = 'light'
      }
    }, {
      label: 'Dark',
      icon: 'i-lucide-moon',
      type: 'checkbox',
      checked: colorMode.value === 'dark',
      onUpdateChecked(checked: boolean) {
        if (checked) {
          colorMode.preference = 'dark'
        }
      },
      onSelect(e: Event) {
        e.preventDefault()
      }
    }]
  }, {
    label: 'Documentation',
    icon: 'i-lucide-book-open',
    to: 'https://docs.a55.tech/',
    target: '_blank'
  }], [{
    label: 'Privacy Policy',
    icon: 'i-lucide-shield',
    to: '/privacy',
    target: '_blank'
  }, {
    label: 'Cookie Settings',
    icon: 'i-lucide-cookie',
    onSelect() {
      emit('openCookieSettings')
    }
  }], [{
    label: 'Log out',
    icon: 'i-lucide-log-out',
    onSelect: logout
  }]]
})

const getChipStyle = (item: { chip: string }) => ({
  '--chip-light': `var(--color-${item.chip}-500)`,
  '--chip-dark': `var(--color-${item.chip}-400)`
})
</script>

<template>
  <UDropdownMenu
    v-if="user"
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{ content: collapsed ? 'w-48' : 'w-(--reka-dropdown-menu-trigger-width)' }"
  >
    <UButton
      v-bind="{
        ...user,
        label: collapsed ? undefined : user?.name,
        trailingIcon: collapsed ? undefined : 'i-lucide-chevrons-up-down'
      }"
      color="neutral"
      variant="ghost"
      block
      :square="collapsed"
      class="data-[state=open]:bg-elevated"
      :ui="{
        trailingIcon: 'text-dimmed'
      }"
    />

    <template #chip-leading="{ item }">
      <span
        :style="getChipStyle(item)"
        class="ms-0.5 size-2 rounded-full bg-(--chip-light) dark:bg-(--chip-dark)"
      />
    </template>
  </UDropdownMenu>
</template>
